import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Block 0 Phase 3 (ADR-0013 §5) — capture `beforeinstallprompt` + `appinstalled`.
 *
 * Минимальный тип для нестандартного DOM event'а. Не покрыт `lib.dom.d.ts`.
 * Эмитится только Chromium-based браузерами (Android Chrome / Edge / Samsung
 * Internet / Desktop Chrome). iOS Safari и Firefox event не выбрасывают —
 * это ожидаемо, и Container в Step 3 покрывает iOS отдельной веткой
 * (IOSInstructions); Firefox остаётся на fallback «PWA-установка недоступна».
 *
 * Spec: https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface UseInstallPromptResult {
  /**
   * Сохранённый `beforeinstallprompt` event. `null` до того, как браузер
   * его эмитит, или сразу после `triggerInstall()` (single-shot consume).
   */
  prompt: BeforeInstallPromptEvent | null;
  /**
   * `true` после события `appinstalled` ИЛИ после `userChoice.outcome === 'accepted'`.
   * Idempotent: повторные события не перезаписывают `false` поверх `true`.
   */
  installed: boolean;
  /**
   * Запускает нативный диалог установки. Single-shot — повторный вызов
   * до следующего `beforeinstallprompt` вернёт `'unavailable'`.
   *
   * Возможные исходы:
   * - `'accepted'` — пользователь подтвердил установку.
   * - `'dismissed'` — пользователь отказался.
   * - `'unavailable'` — `prompt` ещё не captured / уже consumed / `prompt()`
   *   бросил исключение (Chrome бросает при повторном вызове на том же event).
   */
  triggerInstall: () => Promise<InstallOutcome>;
}

/**
 * Capture'ит `beforeinstallprompt` (preventDefault — блокирует дефолтный
 * mini-infobar Chrome) и сохраняет event для последующего `triggerInstall()`.
 * Параллельно слушает `appinstalled` — fire'ится после успешной установки
 * через любой UI (наш кнопки, mini-infobar, browser settings menu).
 *
 * Race-condition guards:
 *
 * 1. **Single-shot:** `triggerInstall()` сразу set'ит `prompt=null` и
 *    помечает `inFlightRef.current=true`. Повторный синхронный клик до
 *    разрешения `userChoice` вернёт `'unavailable'` (двойной prompt() на
 *    одном event бросит DOM-исключение, и Chrome закроет наш диалог
 *    некорректно).
 *
 * 2. **Unmount during await:** mountedRef проверяется перед каждым setState.
 *    Если компонент размонтирован пока `prompt.prompt()` или `userChoice`
 *    pending — outcome возвращается, но state не обновляется.
 *
 * 3. **Idempotent appinstalled:** `setInstalled(true)` срабатывает один
 *    раз практически из-за React-equality check; даже если event fire'ится
 *    несколько раз (Chrome иногда дублирует), state стабилен.
 *
 * 4. **Multiple beforeinstallprompt:** Chrome может re-fire (например,
 *    после navigation внутри SPA). Мы перезаписываем `prompt` свежим event'ом
 *    — старый устаревает (browser invalid'ирует prompt() на нём).
 */
export function useInstallPrompt(): UseInstallPromptResult {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  const inFlightRef = useRef<boolean>(false);

  useEffect(() => {
    mountedRef.current = true;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (!mountedRef.current) return;
      setPrompt(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      if (!mountedRef.current) return;
      setInstalled(true);
      setPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async (): Promise<InstallOutcome> => {
    if (prompt === null || inFlightRef.current) {
      return 'unavailable';
    }
    inFlightRef.current = true;

    // Consume event сразу — даже если await зависнет, повторные клики через
    // closure-замкнутые triggerInstall не запустят второй prompt().
    const captured = prompt;
    setPrompt(null);

    try {
      await captured.prompt();
      const choice = await captured.userChoice;
      if (mountedRef.current && choice.outcome === 'accepted') {
        setInstalled(true);
      }
      return choice.outcome;
    } catch {
      return 'unavailable';
    } finally {
      inFlightRef.current = false;
    }
  }, [prompt]);

  return { prompt, installed, triggerInstall };
}
